import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";

function formatDate(value) {
    if (!value) {
        return "-";
    }

    return String(value).replace("T", " ").slice(0, 19);
}

function formatXml(xmlString) {
    if (!xmlString) {
        return "";
    }

    const withBreaks = xmlString.replace(/>\s*</g, ">\r\n<");
    let pad = 0;
    let formatted = "";

    withBreaks.split("\r\n").forEach((node) => {
        let indentChange = 0;

        if (node.match(/.+<\/\w[^>]*>$/)) {
            indentChange = 0;
        } else if (node.match(/^<\/\w/)) {
            if (pad !== 0) {
                pad -= 1;
            }
        } else if (node.match(/^<\w[^>]*[^/]>.*$/)) {
            indentChange = 1;
        }

        formatted += "    ".repeat(pad) + node + "\r\n";
        pad += indentChange;
    });

    return formatted.trim();
}

function getStatusClass(status) {
    const value = String(status || "").toLowerCase();

    if (value.includes("fail") || value.includes("error")) {
        return "bg-red-100 text-red-800";
    }

    if (value.includes("success") || value.includes("valid")) {
        return "bg-green-100 text-green-800";
    }

    return "bg-gray-100 text-gray-800";
}

function XmlLogSection({ title, xml, summary }) {
    const [showXml, setShowXml] = useState(false);

    return (
        <div className="border border-gray-200">
            <div className="flex flex-col gap-2 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold uppercase text-gray-700">{title}</h3>

                    {summary?.status && (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold ${getStatusClass(summary.status)}`}>
                            {summary.status}
                        </span>
                    )}
                </div>

                {xml && (
                    <button
                        type="button"
                        onClick={() => setShowXml((value) => !value)}
                        className="text-xs font-medium text-lk_red hover:text-lk_red_hover uppercase text-left"
                    >
                        {showXml ? "Hide XML" : "Show XML"}
                    </button>
                )}
            </div>

            <div className="space-y-3 px-4 py-3">
                {!xml && (
                    <p className="text-sm text-gray-500">
                        No XML was recorded for this section.
                    </p>
                )}

                {summary?.parse_error && (
                    <p className="text-sm text-red-700">
                        The stored XML could not be parsed. The raw content is shown below.
                    </p>
                )}

                {summary?.errors?.length > 0 && (
                    <div>
                        <div className="text-xs font-medium uppercase text-red-700">Errors</div>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-red-700">
                            {summary.errors.map((message, index) => (
                                <li key={index}>{message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {summary?.warnings?.length > 0 && (
                    <div>
                        <div className="text-xs font-medium uppercase text-yellow-700">Warnings</div>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-yellow-700">
                            {summary.warnings.map((message, index) => (
                                <li key={index}>{message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {xml && summary && !summary.parse_error && summary.errors?.length === 0 && summary.warnings?.length === 0 && (
                    <p className="text-sm text-gray-500">
                        No warnings or errors were reported by Syspro.
                    </p>
                )}

                {xml && showXml && (
                    <pre className="max-h-96 overflow-auto border border-gray-200 bg-gray-900 p-3 text-xs leading-5 text-green-200">
                        {summary?.parse_error ? xml : formatXml(xml)}
                    </pre>
                )}
            </div>
        </div>
    );
}

export function SysproOrderLog() {
    const { currentUser } = useSelector((state) => state.auth);
    const token = currentUser?.token;
    const [orderNo, setOrderNo] = useState("");
    const [records, setRecords] = useState([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    async function searchOrder(event) {
        event.preventDefault();

        const trimmedOrderNo = orderNo.trim();

        if (!trimmedOrderNo || !token) {
            return;
        }

        setLoading(true);
        setErrorMessage("");
        setSearched(false);

        try {
            const response = await axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}admin/syspro-order-log`,
                headers: { Authorization: `Bearer ${token}` },
                params: { order_no: trimmedOrderNo },
            });

            setRecords(response.data?.records || []);
        } catch (error) {
            setRecords([]);
            setErrorMessage(
                error?.response?.data?.message ||
                "Syspro order log could not be loaded."
            );
        } finally {
            setLoading(false);
            setSearched(true);
        }
    }

    return (
        <div className="bg-white">
            <div className="my-8 space-y-6 px-4 sm md:max-w-2xl lg:max-w-4xl xl:max-w-6xl m-auto">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <h1>Syspro Order Log</h1>

                    <a
                        className="px-4 py-2 border border-transparent text-sm font-medium border_radius_4 shadow-sm text-white bg-lk_grey_one hover:bg-lk_dark_grey_button_hover uppercase text-center"
                        href="/admin-dashboard"
                    >
                        Back to Admin
                    </a>
                </div>

                <p className="text-sm text-gray-600">
                    Enter a Syspro order number to view the XML responses recorded when the
                    order was pushed to Syspro (the order post and the lot traceable back
                    order release).
                </p>

                <form onSubmit={searchOrder} className="flex flex-col gap-3 border border-gray-200 p-4 sm:flex-row sm:items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="order_no">
                            Order Number
                        </label>
                        <input
                            id="order_no"
                            type="text"
                            value={orderNo}
                            onChange={(event) => setOrderNo(event.target.value)}
                            placeholder="Example: 000000012345"
                            className="mt-1 w-full border border-gray-300 rounded p-2 uppercase"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !orderNo.trim()}
                        className="px-4 py-2 border border-transparent text-sm font-medium border_radius_4 shadow-sm text-white bg-lk_red hover:bg-lk_red_hover uppercase disabled:opacity-50"
                    >
                        {loading ? "Searching..." : "Search"}
                    </button>
                </form>

                {errorMessage && (
                    <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {errorMessage}
                    </div>
                )}

                {searched && !errorMessage && records.length === 0 && (
                    <div className="border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                        No tracking records were found for that order number.
                    </div>
                )}

                {records.map((record) => (
                    <div key={record.id} className="space-y-4 border border-gray-200 p-4">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <div className="text-xs font-medium uppercase text-gray-500">Order Number</div>
                                <div className="mt-1 text-sm font-medium text-gray-900">{record.order_no || "-"}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium uppercase text-gray-500">Customer PO</div>
                                <div className="mt-1 text-sm text-gray-900">{record.order_po_no || "-"}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium uppercase text-gray-500">Customer Code</div>
                                <div className="mt-1 text-sm text-gray-900">{record.customer_code || "-"}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium uppercase text-gray-500">Order Value</div>
                                <div className="mt-1 text-sm text-gray-900">{record.order_value || "-"}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium uppercase text-gray-500">Placed By</div>
                                <div className="mt-1 text-sm text-gray-900">{record.placed_by || "-"}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium uppercase text-gray-500">Placed At</div>
                                <div className="mt-1 text-sm text-gray-900">{formatDate(record.created_at)}</div>
                            </div>
                        </div>

                        <XmlLogSection
                            title="Order Post (SORTOI)"
                            xml={record.syspro_log}
                            summary={record.syspro_log_summary}
                        />

                        <XmlLogSection
                            title="Lot Traceable Release (SORTBO)"
                            xml={record.syspro_log_traceable}
                            summary={record.syspro_log_traceable_summary}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
